import React, {useState, useContext, useEffect, useCallback, useRef} from 'react'
import { LocationContext } from '../../../context/LocationContext';
import { setLocalStorageEmail, setLocalStorageLoginToken, setLocalStorageJwtToken, setLocalStorageProfilePic } from '../../../js/localstorage';
import { loginState } from '../../../api/user/login-state';
import { RequestContext } from '../../../context/RequestContext';
import { useRouter } from 'next/router';
import { uploadImage } from '../../../api/user/post-api';
import { LoadingCover } from '../../commons/LoadingCover';
import { sendNewRequestEmail, userActivateEmail } from '../../../api/email/email-api';
import { SystemError } from '../../error/SystemError';
import { registerUser } from '../../../api/user/register-api';
import { loginWithGoogle } from '../../../api/user/googleloogin-api';
import axios from 'axios';
import { uploadRequest, uploadRequestMedia, uploadRequestWithServiceItems } from '../../../api/request/request-api';
import { parseJwt } from '../../../service/parse-jwt';
import { motion, AnimatePresence } from 'framer-motion';
import { googleCaptchaKey, s3Static } from '../../../utils/constants';
import Image from 'next/image';
import Link from 'next/link';
import Script from 'next/script';
import { UploadingMediaLoader } from '../../commons/UploadingMediaLoader';
import { ChevronLeft, User, Mail, CheckCircle, Shield } from 'lucide-react';
import { PriceCard, ModernInput } from '../../commons/BookingFormComponents';
import { nextRequiredField } from '../../../utils/nextRequiredField';
import { iosLogin } from '../../../api/firebase/firebase';

// Helper to get criteria value
const getCriteriaValue = (requestCriteria, questionCode) => {
    if (!requestCriteria || !Array.isArray(requestCriteria)) return null
    for (let i = 0; i < requestCriteria.length; i++) {
        const item = requestCriteria[i]
        if (item && item[questionCode] !== undefined && item[questionCode] !== null) {
            return item[questionCode]
        }
    }
    return null
}

// Animation variants - simplified to prevent layout shifts
const containerVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            staggerChildren: 0.05,
            delayChildren: 0.05
        }
    }
};

const itemVariants = {
    initial: { opacity: 0 },
    animate: { 
        opacity: 1,
        transition: {
            duration: 0.2
        }
    }
};

// Terms Agreement Component - Compact
const TermsAgreement = () => {
    return (
        <motion.div 
            variants={itemVariants}
            className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg"
        >
            <div className="shrink-0 w-5 h-5 rounded bg-green-500 flex items-center justify-center mt-0.5">
                <CheckCircle className="w-3 h-3 text-white" />
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
                By continuing, I agree to the{' '}
                <Link href="/terms" className="text-orange-600 hover:text-orange-700 font-medium" target="_blank" rel="noopener">Terms</Link>
                {' '}and{' '}
                <Link href="/privacy-policy" className="text-orange-600 hover:text-orange-700 font-medium" target="_blank" rel="noopener">Privacy Policy</Link>
            </p>
        </motion.div>
    );
};

// Divider Component
const OrDivider = () => {
    return (
        <div className="relative py-4">
            <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center">
                <span className="px-4 bg-white text-sm text-gray-400">or continue with</span>
            </div>
        </div>
    );
};

export const QuickSignIn = (props) => {
    const router = useRouter();
    const affiliateId = router.query.affiliateId
    const referrerId = router.query.referrerId
    const requestCategorySettings = props.requestCategorySettings;
    const goToSection2 = props.goToSection2
    // Previous step; goToSection2 (start over) for callers that do not pass it
    const goBack = props.goBack || goToSection2
    const variant = props.variant || "light"  // "light" | "dark"
    const isDark = variant === "dark"
    const googleBtnId = isDark ? "ptButtonDiv" : "buttonDiv"

    const [ businessType, setBusinessType ] = useState("")
    const [ isGoogleBtnLoading, setIsGoogleBtnLoading ] = useState(true)
    const [ isLoading, setIsLoading ] = useState(false);
    const [ userFullName, setUserFullName ] = useState("");
    const [ userId, setUserId ] = useState("");
    const [ profilePicUrl, setProfilePicUrl ] = useState(undefined);
    const [ isNewFbGoogleUser, setIsNewFbGoogleUser ] = useState(false);
    const [ formValues, setFormValues] = useState({ emailId: "", userFullName: ""});
    // The field to point at next (pulsing ring, no autofocus: focusing opens
    // the phone keyboard over the step before it has been read)
    const nextField = nextRequiredField([
        { name: "userFullName", done: !!formValues.userFullName?.trim() },
        { name: "emailId", done: !!formValues.emailId?.trim() },
    ])
    const [ isSubmit, setIsSubmit ] = useState(false);
    const [ formErrors, setFormErrors ] = useState({});  
    const [ captchaToken, setCaptchaToken ] = useState(""); 
    const [ uploadPercent, setUploadPercent ] = useState(0);
    const [ isUploadingMedia, setIsUploadingMedia ] = useState(false);

    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""});    

    const { countryName, countryCd, countryCurrCd, countryTelCd, userLatitude, userLongitude } = useContext(LocationContext);
    
    const { requestType, 
        requestDescription, 
        requestCriteria, 
        userLocation, 
        serviceDate, 
        startTime,
        endTime,
        serviceCost, 
        filesToUpload, 
        setFilesToAttach, 
        setFilesToUpload, 
        originalCost, 
        totalDiscountPercent,
        serviceItems,
        serviceUnitsCount
    } = useContext(RequestContext);
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues({...formValues, [name]: value});
        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors({...formErrors, [name]: ''});
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormErrors(validateForm(formValues));

        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, {action: 'Register'});
            setCaptchaToken(token)
            setIsSubmit(true);
        });
    }

    const validateForm = useCallback(( values ) =>{  
        const errors = {};

        if(!values.emailId){
            errors.emailId = "Email Id is required for important communications."
        }
        if(!values.emailId.match(/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/)){
            errors.emailId = "Invalid Email Id."
        }
        if(values.userFullName.length < 2){
            errors.userFullName = "Your name should be at least 2 characters in length."
        }
        return errors;
    }, [])

    const callNativeApp = (email) => {
        return new Promise((resolve) => {
            try {
                const fcmToken = window.iosFCMToken || '';
                if (fcmToken && fcmToken.trim() !== '') {
                    iosLogin(email, fcmToken)
                        .then(() => console.log('IosLogin success'))
                        .catch(err => console.log('IosLogin error:', err));
                }
                if (typeof webkit !== 'undefined' && webkit.messageHandlers && webkit.messageHandlers.callbackHandler) {
                    webkit.messageHandlers.callbackHandler.postMessage(email);
                }
                setTimeout(resolve, 500);
            } catch(err) {
                console.log('callNativeApp error:', err.message);
                resolve();
            }
        });
    }

    const goToHome = useCallback(() => {
        setIsLoading(true)
        router.push("/home")
        .then(() => setIsLoading(false))
    }, [router, setIsLoading])

    const requestUploadComplete = useCallback((requestIdInt) => {
        if(businessType === 'lead' && isNewFbGoogleUser){
            sendNewRequestEmail(userId, requestIdInt, requestType, requestDescription);                
        }

        //track conversion
        try {
            switch (requestType) {
                case "Aircon Services":
                    gtag('event', 'conversion', {
                        'send_to': 'AW-823304067/mcm9CL_X3YsYEIO_yogD',
                        'transaction_id': userId
                    })
                    try {
                        window.uetq = window.uetq || [];
                        window.uetq.push("event", "purchase", {});
                    } catch (error) {
                        console.log(error)
                    }
                    break;
                case "Domestic Services":
                    fbq('track', 'CompleteRegistration')
                    
                    gtag('event', 'conversion', {
                        'send_to': 'AW-823304067/V2pfCLndyPgYEIO_yogD',
                        'transaction_id': userId
                    })
                    break;
                case "Fitness Trainer":
                    gtag('event', 'conversion', {
                        'send_to': 'AW-823304067/DthiCPas6oIBEIO_yogD',
                        'transaction_id': userId
                    });
                    break;
            }
        } catch (error) {
            console.log("Unable to track conversion")
        }        

        setTimeout(() => {
            setIsLoading(true);
            if(referrerId){
                router.push(`/home?newRequest=true&newRequestIdInt=${requestIdInt}&referrerId=${referrerId}`)
                    .then(() => setIsLoading(false))
            } else {
                if(affiliateId){
                    router.push(`/home?newRequest=true&newRequestIdInt=${requestIdInt}&affiliateId=${affiliateId}`)
                    .then(() => setIsLoading(false))
                } else {
                    router.push(`/home?newRequest=true&newRequestIdInt=${requestIdInt}`)
                    .then(() => setIsLoading(false))
                }
            }
        }, 1000);
    },[businessType, isNewFbGoogleUser, userId, requestType, requestDescription, referrerId, affiliateId, router])

    const uploadRequestMediaFile = useCallback(async(x, requestIdInt) => {
        try {
            let response = await uploadRequestMedia(userId, requestIdInt, filesToUpload[x].fileType, filesToUpload[x].media)
            setUploadPercent(Math.round(((x + 1)/(filesToUpload.length)) * 100) + "%")
            if( response == "success" && (filesToUpload.length - 1) === x){
                setIsUploadingMedia(false)
                setFilesToUpload([])
                requestUploadComplete(requestIdInt)
            } else {
                if(response === "success"){
                    x++
                    uploadRequestMediaFile(x, requestIdInt)
                }
            }
        } catch (error) {
            console.log(error)
            setFilesToAttach([])
            setFilesToUpload([])
            setErrorContent({'header': "Error uploading file", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"})
            setIsSystemError(true)
        }	
    }, [userId, filesToUpload, requestUploadComplete, setFilesToAttach, setFilesToUpload])

    const uploadNewRequest = useCallback(async () => {        
        try {
            const isMediaPresent = filesToUpload.length > 0
            
            const requestIdInt = serviceItems?.length > 0
                ? await uploadRequestWithServiceItems(
                    userId,
                    serviceUnitsCount,
                    originalCost,
                    requestType,
                    requestDescription,
                    "job",
                    countryName,
                    countryCurrCd,
                    requestCriteria,
                    serviceItems,
                    serviceDate,
                    startTime,
                    endTime,
                    0,   //platformFeePercent
                    false,  //isPrePaymentNeeded  
                    false   //isOnBehalfRequest  
                )
                : 
                await uploadRequest(
                    requestType,
                    requestDescription,
                    requestCriteria,
                    businessType,
                    countryName,
                    countryCurrCd,
                    serviceDate,
                    startTime,
                    endTime,
                    isMediaPresent
                );

            setFilesToAttach([])
            if( requestIdInt > 1 && filesToUpload.length > 0) {
                setIsUploadingMedia(true)
                uploadRequestMediaFile(0, requestIdInt)
            } else {
                if( requestIdInt > 1) {
                    requestUploadComplete(requestIdInt)
                }
            }
            if( requestIdInt === 1){
                setIsLoading(false);
                goToHome() 
            }
            if( requestIdInt === 0){
                setIsLoading(false);
                setErrorContent({'header': "Upload Request Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
                setIsSystemError(true);
            }
        } catch (error) {
            console.log(error)
            setErrorContent({'header': "Error submitting request", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }
    }, [requestType, requestDescription, requestCriteria, goToHome, countryCurrCd, businessType, originalCost, serviceItems, userId,
        countryName, serviceDate, startTime, endTime, filesToUpload, setFilesToAttach, requestUploadComplete, uploadRequestMediaFile, serviceUnitsCount] )    

    const getProfilePicFile = useCallback(async() => {                
        const config = { responseType: 'blob' };
        axios.get(profilePicUrl, config).then( async(response) => {
            let file = response.data;
            try{
                await uploadImage(file, userId, userFullName, "Profile Picture", "Profile", "Public", "strings", "My new Profile Picture");
            } catch (error){
                console.log(error);
                setErrorContent({'header': "Error uploading image", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
                setIsSystemError(true);
                setIsLoading(false);
                return;
            }            
            if(requestType != undefined) {
                uploadNewRequest();
            } else {
                goToHome()
            }    
        }).catch((error) => {
            console.log(error)
            if(requestType != undefined) {
                uploadNewRequest();
            } else {
                goToHome()
            } 
        })
    }, [userId, userFullName, profilePicUrl, requestType, uploadNewRequest, goToHome])

    const processUserId = useCallback(() =>{
        if(isNewFbGoogleUser) {
            getProfilePicFile();
        } else {
            if(requestType != undefined) {
                uploadNewRequest();
            } else {
                goToHome()
            }
        }      
    }, [isNewFbGoogleUser, requestType, getProfilePicFile, uploadNewRequest, goToHome])

    const getUserId = async() => {
        try{
            let loginStateResponse = await loginState();    
            setUserId(loginStateResponse.id); 
        } catch (error){
            console.log(error);
            setErrorContent({'header': "Cannot get UserId", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }
    }

    const createAccount = useCallback(async() => {
        if(!captchaToken) return

        try {            
            gtag('event', 'conversion', {'send_to': 'AW-823304067/3YR1CLWy280DEIO_yogD'});
        } catch (error) {
            console.log(error)
        }

        setIsLoading(true);
        let registerResponse;
        try {
            registerResponse = await registerUser(
                  formValues.emailId
                , ""
                , formValues.userFullName
                , ""
                , "General"
                , countryName
                , countryCd
                , countryCurrCd
                , countryTelCd
                , userLocation
                , userLatitude
                , userLongitude
                , captchaToken
            );
        } catch (error){
            console.log(error);
            setErrorContent({'header': "User Registration Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 0){
            setErrorContent({'header': "System Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 2){
            setErrorContent({'header': "Email id is taken", 'body': "This email id is already registered! Try with a different email id."});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }

        if(registerResponse.responseCode == 1) {
            setLocalStorageEmail(formValues.emailId);
            setLocalStorageLoginToken(registerResponse.loginToken);
            setLocalStorageJwtToken(registerResponse.jwtToken);

            userActivateEmail(formValues.emailId, formValues.userFullName);
            getUserId();
        } else {
            setErrorContent({'header': "System Error", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            return;
        }        
    },  [formValues
        , countryName
        , countryCd
        , countryCurrCd
        , countryTelCd
        , userLocation
        , userLatitude
        , userLongitude
        , captchaToken
        ])
    

    const loginGoogleUser = useCallback(async(email, name, profilePicUrl, googleLoginCaptcha) => {
        try {            
            gtag('event', 'conversion', {'send_to': 'AW-823304067/3YR1CLWy280DEIO_yogD'});
        } catch (error) {
            console.log(error)
        }
        
        setIsLoading(true);

        let googleLoginResponse;
        try {
            googleLoginResponse = await loginWithGoogle(email, name, profilePicUrl, countryName, countryCd, countryCurrCd, countryTelCd, userLocation, userLatitude, userLongitude, googleLoginCaptcha);
        } catch {
            console.log("Google Login Error");
            setErrorContent({'header': "Google Login Problem", 'body': "Please try again. If problem persists, contact us at support@stringssg.com"});
            setIsSystemError(true);
            setIsLoading(false);
            setIsSubmit(false);
            return;
        }
        
        setLocalStorageEmail(email);
        setLocalStorageLoginToken(googleLoginResponse.loginToken);
        setLocalStorageJwtToken(googleLoginResponse.jwtToken);
        setLocalStorageProfilePic(profilePicUrl);
        setProfilePicUrl(profilePicUrl);

        setUserFullName(name);

        if (typeof android != "undefined"){
            android.showToast(email);
        }
        await callNativeApp(email);
        
        if(googleLoginResponse.responseCode === 1){
            setIsNewFbGoogleUser(true);
        }
        getUserId();
    }, [countryName, countryCd, countryCurrCd, countryTelCd, userLocation, userLatitude, userLongitude])

    const handleCredentialResponse = useCallback((response) => {
        const decodedResponse = parseJwt(response.credential);        

        grecaptcha.enterprise.ready(async () => {
            const token = await grecaptcha.enterprise.execute(googleCaptchaKey, {action: 'GoogleLogin'});
            loginGoogleUser(decodedResponse.email, decodedResponse.name, decodedResponse.picture, token)
        });
    }, [loginGoogleUser])

    const initializeGoogleLogin = useCallback(async () => {
        if (typeof google === 'undefined' || !google?.accounts?.id) {
            return;
        }
        setIsGoogleBtnLoading(true);
        try {
            await google.accounts.id.initialize({
                client_id: "97723445335-nmgmbth2t3t6gqjr1jis91to3gj8vf1s.apps.googleusercontent.com",
                callback: handleCredentialResponse
            });
            await google.accounts.id.renderButton(
                document.getElementById(googleBtnId),
                { theme: isDark ? "filled_black" : "outline", size: "large", width: "280", logo_alignment: "left" }
            );
            setIsGoogleBtnLoading(false);            
        } catch (error) {
            setIsGoogleBtnLoading(false);
            console.log(error)
        }
    }, [handleCredentialResponse])

    useEffect(() => {
        if(Object.keys(formErrors).length == 0 && isSubmit){
            createAccount();
        } else {
            setIsSubmit(false);
        }
    }, [formErrors, isSubmit, createAccount])

    useEffect(() => {
        if (!userId) return;
        processUserId();
    }, [userId, processUserId]);

    useEffect(() => {
        initializeGoogleLogin();
    }, [initializeGoogleLogin])    

    useEffect(() => {
        if(!requestCategorySettings || !requestType) return
        const getBusinessType = () => {
            for(let x in requestCategorySettings){
                if(requestType === requestCategorySettings[x].requestType){
                    setBusinessType(requestCategorySettings[x].businessType);
                }
            }
        }
        getBusinessType()
        if(requestCriteria.length === 0 && serviceItems.length === 0){
            setErrorContent({'header': "Your requirements data expired", 'body': "Please re-submit the form in 3 secs."});
            setIsSystemError(true);
            setTimeout(() => {
                goToSection2()
            }, [5000])
        }
    }, [requestCategorySettings, requestType, goToSection2, requestCriteria.length, serviceItems.length])

  return (
    <>
        <div className='px-3 pt-2'>
            <form className="max-w-md mx-auto" onSubmit={(e) => handleSubmit(e)}>
                <motion.div 
                    className="space-y-3"
                    variants={containerVariants}
                    initial="initial"
                    animate="animate"
                >
                    {/* Price Card or Header */}
                    {serviceCost > 0 ? (
                        <PriceCard 
                            serviceCost={serviceCost}
                            originalCost={originalCost}
                            totalDiscountPercent={totalDiscountPercent}
                            currencyCode={countryCurrCd}
                        />
                    ) : (
                        <>
                            {(businessType != "job" && businessType != "hybrid" && requestType !== "Fitness Trainer") && (
                                <motion.div 
                                    variants={itemVariants}
                                    className={`flex gap-2 px-3 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-linear-to-br from-green-50 to-emerald-50 border-green-100'}`}
                                >
                                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-lg mb-2 ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                                        <CheckCircle className="w-5 h-5" />
                                    </div>
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-800'}`}>
                                        Great news! We found professionals matching your request
                                    </p>
                                </motion.div>
                            )}
                            {(requestType === "Fitness Trainer") && (
                                <motion.div 
                                    variants={itemVariants}
                                    className={`flex gap-1 px-2 py-2 rounded-xl border ${isDark ? 'bg-green-500/10 border-green-500/20' : 'bg-linear-to-br from-green-50 to-emerald-50 border-green-100'}`}
                                >
                                    <CheckCircle className={`w-4 h-4 shrink-0 mt-0.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-800'}`}>
                                        <span className={`font-bold ${isDark ? 'text-green-300' : 'text-green-600'}`}>Great news! We found a trainer matching your request.</span>
                                    </p>
                                </motion.div>
                            )}
                        </>
                    )}
                    
                    {/* Name Input */}
                    <ModernInput
                        type="text"
                        name="userFullName"
                        placeholder="John Doe"
                        value={formValues.userFullName}
                        onChange={handleChange}
                        icon={User}
                        label="Name"
                        required
                        highlight={nextField === "userFullName"}
                        error={formErrors.userFullName}
                        variant={variant}
                    />
                    
                    {/* Email Input */}
                    <ModernInput
                        type="email"
                        name="emailId"
                        placeholder="john.doe@gmail.com"
                        value={formValues.emailId}
                        onChange={handleChange}
                        icon={Mail}
                        label="Email"
                        required
                        highlight={nextField === "emailId"}
                        error={formErrors.emailId}
                        variant={variant}
                    />
                    
                    {/* Terms Agreement - light mode only */}
                    {!isDark && <TermsAgreement />}
                    
                    {/* Upload Progress */}
                    {isUploadingMedia && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                        >
                            <UploadingMediaLoader uploadPercent={uploadPercent}/>
                        </motion.div>
                    )}
                    
                    {/* Submit Button */}
                    <motion.div variants={itemVariants} className='pt-2'>
                        <motion.button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 px-6 py-4
                                bg-linear-to-r from-orange-500 to-amber-500 
                                hover:from-orange-600 hover:to-amber-600
                                disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed
                                text-white font-bold sm:text-lg uppercase tracking-wide
                                rounded-full shadow-lg shadow-orange-200/50
                                transition-all duration-300"
                            whileHover={!isLoading ? { scale: 1.02 } : {}}
                            whileTap={!isLoading ? { scale: 0.98 } : {}}
                        >
                            {isLoading ? (
                                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <>
                                    {businessType === "job" ? (
                                        <span>Book With Discounts</span>
                                    ) : requestType === "Fitness Trainer" ? (
                                        <span className='text-lg'>Claim My Free Session</span>
                                    ) : (
                                        <span>Show Deals</span>
                                    )}
                                </>
                            )}
                        </motion.button>
                    </motion.div>
                </motion.div>
                
                {/* Divider */}
                {isDark ? (
                    <div className="relative py-4">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-white/10"></div>
                        </div>
                        <div className="relative flex justify-center pt-2">
                            <span className="px-4 text-sm text-white/30" style={{ backgroundColor: 'rgb(15 23 42)' }}>or continue with</span>
                        </div>
                    </div>
                ) : (
                    <OrDivider />
                )}
                
                {/* Google Sign In Button - fixed height container to prevent layout shift */}
                <div className="flex justify-center py-2 min-h-12">
                    {isGoogleBtnLoading && (
                        <div className="flex justify-center items-center w-70 h-10">
                            <div className="w-5 h-5 border-2 border-gray-300 border-t-orange-500 rounded-full animate-spin" />
                        </div>
                    )}
                    <div id={googleBtnId} className="flex justify-center w-70 h-10" style={{ display: isGoogleBtnLoading ? 'none' : 'flex' }}></div> 
                </div>
                
                {/* Security Badge */}
                <motion.div 
                    className={`flex items-center justify-center gap-2 mt-2 text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    <Shield className="w-4 h-4" />
                    <span>Your information is secure and encrypted</span>
                </motion.div>
            </form>
            
            {/* Back Button */}
            <motion.div 
                className="flex justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
            >
                <motion.button
                    type="button"
                    onClick={() => goBack()}
                    className={`flex items-center gap-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                        isDark 
                            ? 'text-white/40 hover:text-orange-400 hover:bg-white/5' 
                            : 'text-gray-500 hover:text-orange-600 hover:bg-orange-50'
                    }`}
                    whileHover={{ x: -3 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <ChevronLeft className="w-5 h-5" />
                    <span className="font-medium">Back</span>
                </motion.button>
            </motion.div>
            
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
            {isLoading && <LoadingCover/>}
        </div>
        <Script
            id="google-captcha"
            src={`https://www.google.com/recaptcha/enterprise.js?render=${googleCaptchaKey}`}
            strategy="lazyOnload"
        />
    </>
  )
}
